import { CAD_PARCEL_STOCK_STYLE } from '../../Map/styles/mapTheme';
import { CAD_STOCK_STATUS_ITEMS, CAD_STOCK_STATUSES } from '../data/cadConstants';
import { isCadSellableLot } from './cadMasterplan';

const IS_FILTER_MATCH = ['boolean', ['get', 'filter_match'], true];
const STOCK_STATUS_GET = ['coalesce', ['get', 'stock_status'], 'available'];

/**
 * Color de stock (mismo token que el overlay). Estado desconocido → available.
 * @param {string|null|undefined} statusId
 * @returns {string}
 */
export function getCadStockColor(statusId) {
  const id = CAD_STOCK_STATUSES.includes(statusId) ? statusId : 'available';
  return CAD_PARCEL_STOCK_STYLE.stockFill[id]
    || CAD_PARCEL_STOCK_STYLE.stockFill.available;
}

/**
 * Estados presentes en el GeoJSON, en el orden del catálogo.
 * Un `stock_status` desconocido cuenta como `available`.
 * @param {{ features?: Array<{ properties?: { stock_status?: string } }> }|null|undefined} collection
 * @returns {{ id: string, label: string, color: string }[]}
 */
export function getCadStockLegendItems(collection) {
  const features = Array.isArray(collection?.features) ? collection.features : [];
  if (!features.length) return [];

  const present = new Set();
  features.forEach((feature) => {
    if (!isCadSellableLot(feature)) return;
    const raw = feature?.properties?.stock_status;
    const id = CAD_STOCK_STATUSES.includes(raw) ? raw : 'available';
    present.add(id);
  });

  return CAD_STOCK_STATUS_ITEMS
    .filter((item) => present.has(item.id))
    .map((item) => ({
      id: item.id,
      label: item.label,
      color: getCadStockColor(item.id),
    }));
}

/**
 * Ids activos para paint. Vacío o todos-off = todos.
 * @param {Record<string, boolean>|null|undefined} visibility
 * @returns {string[]}
 */
export function visibleCadStockStatusIds(visibility) {
  if (!visibility || typeof visibility !== 'object') return [...CAD_STOCK_STATUSES];
  const ids = CAD_STOCK_STATUSES.filter((id) => visibility[id] !== false);
  return ids.length ? ids : [...CAD_STOCK_STATUSES];
}

/**
 * ¿El estado está visible en la leyenda / paint?
 * @param {Record<string, boolean>|null|undefined} visibility
 * @param {string} statusId
 * @returns {boolean}
 */
export function isCadStockStatusVisible(visibility, statusId) {
  if (!visibility || typeof visibility !== 'object') return true;
  return visibility[statusId] !== false;
}

/**
 * Toggle de un estado en la leyenda. `null` = todos visibles.
 * Si quedarían todos on u off, vuelve a `null`.
 *
 * @param {Record<string, boolean>|null|undefined} visibility
 * @param {string} statusId
 * @returns {Record<string, boolean>|null}
 */
export function toggleCadStockVisibility(visibility, statusId) {
  if (!CAD_STOCK_STATUSES.includes(statusId)) {
    return visibility && typeof visibility === 'object' ? { ...visibility } : null;
  }
  const next = {};
  CAD_STOCK_STATUSES.forEach((id) => {
    next[id] = visibility == null ? true : visibility[id] !== false;
  });
  next[statusId] = !next[statusId];
  const allOn = CAD_STOCK_STATUSES.every((id) => next[id]);
  const allOff = CAD_STOCK_STATUSES.every((id) => !next[id]);
  if (allOn || allOff) return null;
  return next;
}

/**
 * Pasto del lote disponible según `tone_seed`. Reservado / vendido / oferta
 * siguen con un color plano: ahí el estado tiene que leerse sin ruido.
 * @returns {unknown[]|string}
 */
function availableFillExpression() {
  const tones = Array.isArray(CAD_PARCEL_STOCK_STYLE.grassTones)
    ? CAD_PARCEL_STOCK_STYLE.grassTones
    : [];
  if (tones.length < 2) return CAD_PARCEL_STOCK_STYLE.stockFill.available;
  const expr = ['match', ['coalesce', ['get', 'tone_seed'], 0]];
  tones.forEach((tone, index) => {
    if (index === tones.length - 1) return;
    expr.push(index, tone);
  });
  expr.push(tones[tones.length - 1]);
  return expr;
}

export function buildCadFillColorExpression(visibleIds) {
  const byStock = ['match', STOCK_STATUS_GET];
  CAD_STOCK_STATUS_ITEMS.forEach((item) => {
    const flat = CAD_PARCEL_STOCK_STYLE.stockFill[item.id]
      || CAD_PARCEL_STOCK_STYLE.stockFill.available;
    byStock.push(item.id, item.id === 'available' ? availableFillExpression() : flat);
  });
  byStock.push(availableFillExpression());
  return [
    'case',
    isMutedExpr(visibleIds), CAD_PARCEL_STOCK_STYLE.stockFillMuted,
    byStock,
  ];
}

function isStockVisibleExpr(visibleIds) {
  const allowed = Array.isArray(visibleIds)
    ? CAD_STOCK_STATUSES.filter((id) => visibleIds.includes(id))
    : [...CAD_STOCK_STATUSES];
  const ids = allowed.length ? allowed : [...CAD_STOCK_STATUSES];
  if (ids.length >= CAD_STOCK_STATUSES.length) return true;
  return ['in', STOCK_STATUS_GET, ['literal', ids]];
}

function isMutedExpr(visibleIds) {
  const stockVisible = isStockVisibleExpr(visibleIds);
  if (stockVisible === true) return ['!', IS_FILTER_MATCH];
  return ['any', ['!', IS_FILTER_MATCH], ['!', stockVisible]];
}

/**
 * Expresión MapLibre: true si el lote está fuera de filtro / stock oculto.
 * @param {string[]|null|undefined} visibleIds
 * @returns {unknown}
 */
export function buildCadLotMutedExpression(visibleIds) {
  return isMutedExpr(visibleIds);
}

export function buildCadFillOpacityExpression(visibleIds) {
  return [
    'case',
    isMutedExpr(visibleIds), CAD_PARCEL_STOCK_STYLE.fillOpacityMuted,
    ['boolean', ['feature-state', 'selected'], false], CAD_PARCEL_STOCK_STYLE.fillOpacitySelected,
    ['boolean', ['feature-state', 'hover'], false], CAD_PARCEL_STOCK_STYLE.fillOpacityHover,
    CAD_PARCEL_STOCK_STYLE.fillOpacity,
  ];
}

export function buildCadShareFillOpacityExpression(visibleIds) {
  return [
    'case',
    isMutedExpr(visibleIds), CAD_PARCEL_STOCK_STYLE.fillOpacityMuted,
    ['boolean', ['feature-state', 'selected'], false], CAD_PARCEL_STOCK_STYLE.shareFillOpacitySelected,
    ['boolean', ['feature-state', 'hover'], false], CAD_PARCEL_STOCK_STYLE.shareFillOpacityHover,
    CAD_PARCEL_STOCK_STYLE.shareFillOpacity,
  ];
}

export function buildCadLineColorExpression(visibleIds) {
  return [
    'case',
    isMutedExpr(visibleIds), CAD_PARCEL_STOCK_STYLE.lineColorMuted,
    CAD_PARCEL_STOCK_STYLE.lineColor,
  ];
}

export function buildCadLineOpacityExpression(visibleIds) {
  return [
    'case',
    isMutedExpr(visibleIds), CAD_PARCEL_STOCK_STYLE.lineOpacityMuted,
    CAD_PARCEL_STOCK_STYLE.lineOpacity,
  ];
}

export function buildCadShareLineOpacityExpression(visibleIds) {
  return [
    'case',
    isMutedExpr(visibleIds), CAD_PARCEL_STOCK_STYLE.shareLineOpacityMuted,
    CAD_PARCEL_STOCK_STYLE.shareLineOpacity,
  ];
}
